import { useCallback, useMemo } from "react";
import { Route, useHistory, useLocation, useRouteMatch } from "react-router-dom";
import { FullscreenTakeover, QueryLoader } from "@jsq/ds/core";
import { globalEnv } from "~/global-env";
import { ComplianceCaseTypeEnum as CaseTypeEnum } from "~/graphql/supergraphEnums";
import { useGetComplianceCaseQuery } from "~/graphql/supergraphHooks";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { DocManagementProfileCreation } from "../../../documents/DocManagementProfileCreation";
import { deDuplicateDocuments, hasEscalationFilters } from "../utils";
import { DocumentDetailsRightSidebar } from "./DocumentDetailsRightSidebar";
import DocumentManagementDisplay from "./DocumentManagementDisplay";
import DocumentManagementList from "./DocumentManagementList";
import { TaxFormPage } from "./TaxFormPage";

/**
 * This represents a fullscreen taker for the documents management panel. It will display a list of
 * documents and then allow viewing the document and then adding details about each document.
 */
const DocumentManagementFullscreenTakeover = () => {
  const { caseId } = useCurrentCaseContext();
  const history = useHistory();
  const location = useLocation();
  const match = useRouteMatch();

  const storedFilters = useMemo(() => {
    return sessionStorage.getItem("escalationFilters") || "";
  }, []);

  const isEscalationRoute = useMemo(() => {
    return location.pathname.startsWith(`${globalEnv.complianceRoute}/escalations`);
  }, [location.pathname]);

  /**
   * Go back to the documents page of the case we are on when exiting.
   * If the user came from the escalations table with filter parameters,
   * preserve those parameters when navigating back.
   */
  const onExit = useCallback(() => {
    if (isEscalationRoute) {
      const targetPath = `${globalEnv.complianceRoute}/escalations/${caseId}/details`;
      if (hasEscalationFilters(storedFilters)) {
        history.push({
          pathname: targetPath,
          search: storedFilters,
        });
      } else {
        history.push(targetPath);
      }
      return;
    }

    history.push(`${globalEnv.complianceRoute}/case/${caseId}/documents`);
  }, [history, caseId, storedFilters, isEscalationRoute]);
  const complianceCaseResult = useGetComplianceCaseQuery({
    variables: {
      caseId: caseId,
    },
    skip: !caseId || caseId <= 0,
  });

  const caseType = complianceCaseResult.data?.complianceCase?.caseType;
  const investorProfileId = complianceCaseResult.data?.complianceCase?.investingProfile?.id;
  const caseDocuments = complianceCaseResult.data?.complianceCase?.documents ?? [];
  const profileDocuments =
    complianceCaseResult.data?.complianceCase?.investingProfile?.documents ?? [];

  // Combine case and profile documents, ensuring no duplicates based on document ID
  const allDocuments =
    caseType === CaseTypeEnum.Escalation || caseType === CaseTypeEnum.RecurringHit
      ? deDuplicateDocuments(caseDocuments, profileDocuments)
      : caseDocuments;

  // Get documentId from URL query params
  const searchParams = new URLSearchParams(location.search);
  const documentIdStr = searchParams.get("documentId");
  const parsedDocumentId = documentIdStr ? parseInt(documentIdStr, 10) : undefined;
  const documentId = Number.isFinite(parsedDocumentId) ? parsedDocumentId : undefined;

  return (
    <Route path={`${match.path}`}>
      <FullscreenTakeover>
        <FullscreenTakeover.Header onExit={onExit} title={"Document Management"} />
        <QueryLoader result={complianceCaseResult}>
          <FullscreenTakeover.Sidebar style={{ padding: 0, width: "396px" }}>
            <DocumentManagementList
              complianceCase={complianceCaseResult.data?.complianceCase}
              documents={allDocuments}
            />
          </FullscreenTakeover.Sidebar>
          <FullscreenTakeover.Body style={{ padding: 0, gap: 0 }}>
            <DocumentManagementDisplay documents={allDocuments} />
            <DocumentDetailsRightSidebar
              complianceCase={complianceCaseResult.data?.complianceCase}
              documents={allDocuments}
            />
          </FullscreenTakeover.Body>
        </QueryLoader>
      </FullscreenTakeover>
      <Route path={`${match.path}/upsert-profile`}>
        <DocManagementProfileCreation investorProfileId={investorProfileId} />
      </Route>
      <Route path={`${match.path}/tax-form`}>
        <TaxFormPage documentId={documentId} />
      </Route>
    </Route>
  );
};

export default DocumentManagementFullscreenTakeover;
